import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { contacts, deals, players, scouts } from '../data/mockData';
import './Header.css';

const ALL_RESULTS = [
  ...contacts.map(c=>({ type:'Contact', label:`${c.firstName} ${c.lastName}`, sub:c.company, page:'contacts', id:c.id })),
  ...deals.map(d=>({ type:'Deal', label:d.name, sub:`$${d.amount}`, page:'deals', id:d.id })),
  ...players.map(p=>({ type:'Player', label:p.name, sub:p.position, page:'players', id:p.id })),
  ...scouts.map(s=>({ type:'Scout', label:s.name, sub:s.region, page:'scouts', id:s.id })),
];

const typeColor = t => ({ Contact:'#4fc3f7', Deal:'#A7F432', Player:'#f5a623', Scout:'#a78bfa' })[t]||'#8a9a97';

export default function Header({ title, onMenuToggle, onNotif, notifOpen, user, onLogout, onNavigate }) {
  const { token } = useApp();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/app/notifications?limit=1&unread=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setUnreadCount(d.unreadCount || 0);
    } catch(e) {}
  }, [token]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);


  const [search, setSearch]     = useState('');
  const [showUser, setShowUser] = useState(false);
  const [results, setResults]   = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef();
  const now = new Date().toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });

  useEffect(()=>{
    if (search.length < 2) { setResults([]); setShowResults(false); return; }
    const q = search.toLowerCase();
    const found = ALL_RESULTS.filter(r=>r.label.toLowerCase().includes(q)||r.sub.toLowerCase().includes(q)).slice(0,8);
    setResults(found); setShowResults(true);
  },[search]);

  const pick = result => {
    setSearch(''); setShowResults(false);
    onNavigate?.(result.page);
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="menu-btn" onClick={onMenuToggle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-date">{now}</p>
        </div>
      </div>

      <div className="topbar-center" style={{ position:'relative' }}>
        <div className="global-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input ref={searchRef} type="text" placeholder="Search contacts, deals, players..." value={search} onChange={e=>setSearch(e.target.value)} onFocus={()=>search.length>=2&&setShowResults(true)} onBlur={()=>setTimeout(()=>setShowResults(false),150)} />
          {search && <button style={{ background:'none',border:'none',color:'var(--text-dim)',cursor:'pointer',fontSize:16,lineHeight:1 }} onClick={()=>{ setSearch(''); setShowResults(false); }}>×</button>}
        </div>
        {showResults && results.length > 0 && (
          <div style={{ position:'absolute', top:'calc(100% + 8px)', left:0, right:0, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, zIndex:100, boxShadow:'0 8px 24px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            {results.map((r,i)=>(
              <div key={i} onMouseDown={()=>pick(r)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer', borderBottom:i<results.length-1?'1px solid var(--border)':'none' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(167,244,50,0.05)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <span style={{ padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:`${typeColor(r.type)}22`,color:typeColor(r.type),flexShrink:0 }}>{r.type}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13,fontWeight:500,color:'var(--text-primary)',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis' }}>{r.label}</div>
                  <div style={{ fontSize:11,color:'var(--text-dim)' }}>{r.sub}</div>
                </div>
              </div>
            ))}
            {results.length === 0 && search.length >= 2 && <div style={{ padding:'16px',textAlign:'center',color:'var(--text-dim)',fontSize:13 }}>No results for "{search}"</div>}
          </div>
        )}
      </div>

      <div className="topbar-right">
        <button className={`icon-btn notif-btn ${notifOpen?'active':''}`} onClick={onNotif}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
        </button>

        <div className="user-menu-wrap">
          <button className="avatar topbar-avatar" onClick={() => setShowUser(v => !v)}>HH</button>
          {showUser && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="avatar" style={{width:40,height:40,fontSize:13,background:'rgba(167,244,50,0.15)',color:'var(--accent)'}}>HH</div>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:'var(--text-primary)'}}>{user?.name}</div>
                  <div style={{fontSize:11,color:'var(--text-dim)'}}>{user?.email}</div>
                </div>
              </div>
              <div className="user-dropdown-divider" />
              <button className="user-dropdown-item" onClick={onLogout}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
